import React, { useState } from 'react';
import ModalMaterial from '@mui/material/Modal';
import Box from '@mui/material/Box';
import { axios, endpoints } from 'Api';
import { Trash2, X } from 'Utils/lucideIcons';
import styles from './deleteGroupModal.module.css';

const MODAL_BOX_SX = {
  position: 'absolute',
  top: '50%',
  left: '50%',
  transform: 'translate(-50%, -50%)',
  width: '92%',
  maxWidth: '440px',
  bgcolor: '#ffffff',
  borderRadius: '16px',
  boxShadow: '0 24px 70px rgba(15, 23, 42, 0.22)',
  outline: 'none',
  overflow: 'hidden',
};

/**
 * Delete-group confirmation, in the same visual language as GroupWizardModal
 * (white surface, Montserrat, rounded header icon, ghost + solid footer
 * buttons). Owns the delete request and flips `setDeleting` on success, the
 * same contract the shared ConfirmationModal had.
 */
const DeleteGroupModal = ({
  open,
  group,
  onClose,
  setSnackbar,
  setDeleting,
  cancelLabel = 'Go Back',
  confirmLabel = 'Remove',
}) => {
  const [busy, setBusy] = useState(false);
  const name = group?.name || 'this group';

  const handleDelete = async () => {
    if (busy) return;
    setBusy(true);
    try {
      const { data } = await axios.post(endpoints.deleteMyGroups, { id: group?.id });
      if (data?.success) {
        setDeleting(true);
        setSnackbar({ isVisible: true, message: `${group?.name} removed` });
      } else {
        setSnackbar({ isVisible: true, message: 'Oops! Something went wrong', type: 'error' });
      }
      onClose();
    } catch (e) {
      setSnackbar({ isVisible: true, message: 'Oops! Something went wrong', type: 'error' });
    } finally {
      setBusy(false);
    }
  };

  return (
    <ModalMaterial open={Boolean(open)} onClose={busy ? undefined : onClose}>
      <Box sx={MODAL_BOX_SX}>
        <div className={styles.modal}>
          <div className={styles.header}>
            <div className={styles.headerIcon}>
              <Trash2 size={20} />
            </div>
            <div className={styles.headerText}>
              <h2 className={styles.headerTitle}>Delete group</h2>
              <p className={styles.headerSub}>This can&apos;t be undone</p>
            </div>
            <button type="button" className={styles.closeButton} onClick={onClose} aria-label="Close">
              <X size={16} />
            </button>
          </div>

          <div className={styles.body}>
            <p className={styles.bodyText}>
              Are you sure you want to delete
              {' '}
              <span className={styles.groupName}>{name}</span>
              ?
            </p>
            <p className={styles.bodyHint}>
              The group and its sharing settings will be removed. Members keep their accounts and
              your videos stay in your library.
            </p>
          </div>

          <div className={styles.footer}>
            <button type="button" className={styles.ghostButton} onClick={onClose} disabled={busy}>
              {cancelLabel}
            </button>
            <button type="button" className={styles.dangerButton} onClick={handleDelete} disabled={busy}>
              {busy ? <span className={styles.spinner} /> : <Trash2 size={15} />}
              {busy ? 'Removing…' : confirmLabel}
            </button>
          </div>
        </div>
      </Box>
    </ModalMaterial>
  );
};

export default DeleteGroupModal;
